import { reactive, type Ref, ref, watchEffect } from 'vue'

import { normalizeInboxNotifications } from '@/lib/adapters/notifications.adapter'
import { notificationRequest } from '@/lib/services'
import { novuInstance } from '@/lib/services/novu.services'
import { useUserStore } from '@/stores/user'

export const getNotifications = async (subscriberId: string, page = 1, read?: boolean) => {
  const response = await novuInstance.notifications.list({
    limit: 10,
    offset: (page - 1) * 10,
    read,
    archived: false,
    useCache: false,
  })
  if (response.error) {
    throw response.error
  }
  if (!response.data) {
    throw new Error('Novu returned no notifications')
  }

  return {
    data: normalizeInboxNotifications(response.data.notifications, subscriberId),
    hasMore: response.data.hasMore,
  }
}

export const updateNotificationReadStatus = async (
  subscriberId: string,
  notificationId: string,
  status: 'read' | 'unread',
) => {
  await notificationRequest.put(
    `/subscribers/${subscriberId}/notifications/${notificationId}/read-status`,
    { status },
  )
}

export const markAllNotificationsAsRead = async (subscriberId: string) => {
  await notificationRequest.put(`/subscribers/${subscriberId}/notifications/read-all`, {})
}

export const useGetNotificationStatuses = (campaignIds: Ref<number[]>) => {
  const notificationStatuses = reactive(new Map<number, boolean>())
  const userStore = useUserStore()
  const isLoading = ref(true)
  const error = ref<Error | null>(null)

  const fetchNotificationStatuses = async (subjects: string[], limit = 20) => {
    try {
      isLoading.value = true
      error.value = null

      const { data } = await notificationRequest.post(
        `/subscribers/${userStore.subscriberId}/subject-changes/find-unread`,
        {
          subjects,
          limit,
        },
      )
      data.subjects.forEach((item: { subject: string }) => {
        const campaignId = Number(item.subject.split(':')[1])
        if (campaignId) {
          notificationStatuses.set(campaignId, true)
        }
      })
    } catch (e) {
      error.value = e as Error
      isLoading.value = false
    } finally {
      isLoading.value = false
    }
  }

  watchEffect(() => {
    if (campaignIds.value.length > 0) {
      void fetchNotificationStatuses(campaignIds.value.map((id) => `campaign:${id}`))
    }
  })

  return {
    notificationStatuses,
  }
}
